import { AutoAwesomeMosaic as AutoAwesomeMosaicIcon } from '@mui/icons-material';
import {
  DeleteOutline as DeleteOutlinedIcon,
  DragHandle as DragIcon,
} from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { get, isEmpty } from 'lodash';
import { SidebarAccordion } from 'src/components/accordion';
import type { IGcfProductListGroup } from 'src/interfaces';
import { lang, moveArrayItem, useUserRole } from 'src/utils';

import { ButtonContainer } from '../../ProductsSidebar.styled';
import { CategoryItem } from '../categoryItem/CategoryItem';
import { MultipleCategoriesProductsProps } from './MultipleCategoriesProducts.interface';

export const MultipleCategoriesProducts = (
  props: MultipleCategoriesProductsProps,
) => {
  const enableDnd = props.showComponents.enableColumnList;
  const { isOwnerOperator } = useUserRole();

  // NOTE: Below last group and product rule are only applicable to OO Product Overides to disable user delete actions.
  const hasRealProducts = (group?: IGcfProductListGroup) => {
    if (!group?.products || isEmpty(group.products)) return false;

    return Object.values(group.products).some(
      (p: any) => !p?.config?.isBackupProduct,
    );
  };

  const validGroups = props.groups.filter(hasRealProducts);

  const isLastGroup = validGroups.length === 1;

  const isLastProduct = (groups: IGcfProductListGroup[]): boolean => {
    if (!Array.isArray(groups) || groups.length === 0) return false;

    return groups.some((group) => {
      const realProducts = Object.values(group?.products ?? {}).filter(
        (p: any) => !p?.config?.isBackupProduct,
      );

      return realProducts.length === 1;
    });
  };

  const hideDeleteButton =
    isLastGroup && isLastProduct(validGroups) && isOwnerOperator;

  const handleSortEnd = (oldIndex: number, newIndex: number) => {
    const originalOrders = props.groups.map((group) => group.order);

    const reorderedGroups = moveArrayItem(props.groups, oldIndex, newIndex);

    reorderedGroups.forEach((group, index) => {
      group.order = originalOrders[index];
    });

    props.handleGroupChange('update')(reorderedGroups);
  };

  const handleGroupAdd = () => {
    const newGroup: IGcfProductListGroup = {
      order: props.maxGroupOrder + 1,
      backups: {},
      products: {},
      config: {
        displayOrder: true,
        columnOrder: get(props, 'groups[0].config.columnOrder', 1),
      },
    };

    if (props?.componentConfig?.productList?.config?.groupHeader?.styles) {
      const styles =
        props?.componentConfig?.productList?.config?.groupHeader?.styles;

      const targetGroupHeader = {} as any;

      for (const [key, value] of Object.entries(styles)) {
        targetGroupHeader[key] = value;
      }
      newGroup.config.groupHeader = targetGroupHeader;
    }

    props.handleGroupChange('add')(newGroup);
  };

  const categoryItems = props.groups.map((group, index) => {
    return {
      label: group.config.groupHeader?.text || `Category ${index + 1}`,
      content: (
        <CategoryItem
          hideDeleteButton={hideDeleteButton}
          showComponents={props.showComponents}
          componentType={props.componentType}
          group={group}
          productCategoryIDs={props.productCategoryIDs}
          themeId={props.themeId}
          handleGroupChange={props.handleGroupChange}
          componentConfig={props?.componentConfig}
        />
      ),
      hasError: false,
      dragIcon: <DragIcon />,
      deleteIcon: props.showComponents.showRemoveGroupButton &&
        props.groups.length > 1 &&
        !hideDeleteButton && (
          <DeleteOutlinedIcon
            color="error"
            onClick={() => props.handleGroupChange('delete')(group)}
          />
        ),
      columnId: `${group.order}`,
    };
  });

  return (
    <Box>
      {!isEmpty(props.groups) && (
        <SidebarAccordion
          items={categoryItems}
          enableDnd={enableDnd}
          isColumnList={enableDnd}
          onSortEnd={handleSortEnd}
        />
      )}

      {props.showComponents.showAddGroupButton && (
        <ButtonContainer>
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeMosaicIcon />}
            onClick={handleGroupAdd}>
            {lang.addAdditionalCategory}
          </Button>
        </ButtonContainer>
      )}
    </Box>
  );
};
